/**
 * LayerSplitPanel — visualises the three-tier responsibility model
 * for the learner's current competency.
 *
 * Locked operator brief (Feb 13, 2026):
 *   The learner is proving ONE new responsibility layer at a time.
 *   The AI checks previously unlocked layers remain stable.
 *   Unowned responsibilities are never punished.
 *
 * Renders three pills:
 *   1. Current Focus       — emerald, the new layer being proved
 *   2. Retained Foundation — slate, must stay stable (regression watch)
 *   3. Instructor-led      — neutral, "Not yet your responsibility"
 *
 * Calm and compact. Lives under the focus card on /training so the
 * learner can SEE what's being assessed without browsing a library.
 */
import { Target, ShieldCheck, User } from "lucide-react";


export default function LayerSplitPanel({ layers }) {
  if (!layers) return null;
  const focus = layers.current_focus || [];
  const retained = layers.retained_foundation || [];
  const instructorLed = layers.instructor_led || [];
  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-5"
      data-testid="layer-split-panel"
    >
      <div className="text-xs font-bold tracking-[0.12em] text-slate-500 mb-3">
        WHAT THE AI WILL ASSESS
      </div>

      <Group
        title="Current focus layer"
        subtitle="The new responsibility you're proving"
        items={focus}
        Icon={Target}
        tone="bg-emerald-50 border-emerald-200 text-emerald-900"
        iconTone="text-emerald-700"
        emptyHint="No responsibilities transferred yet"
        testid="layer-current-focus"
      />

      <Group
        title="Retained foundation layers"
        subtitle="Must stay stable while proving the new layer"
        items={retained}
        Icon={ShieldCheck}
        tone="bg-slate-50 border-slate-200 text-slate-800"
        iconTone="text-slate-600"
        emptyHint="None yet — this is your first focus layer"
        testid="layer-retained-foundation"
      />

      <Group
        title="Instructor-led"
        subtitle="Not yet your responsibility — the AI won't score these"
        items={instructorLed}
        Icon={User}
        tone="bg-slate-50/60 border-slate-100 text-slate-500"
        iconTone="text-slate-400"
        emptyHint="None — you own every responsibility for this competency"
        testid="layer-instructor-led"
        muted
      />

      <p className="text-[11px] text-slate-500 italic mt-4 leading-relaxed">
        New responsibility unlocks only when all retained foundations
        remain stable in the same drive.
      </p>
    </div>
  );
}

function Group({ title, subtitle, items, Icon, tone, iconTone, emptyHint, testid, muted = false }) {
  return (
    <div className="mb-4 last:mb-0" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} className={iconTone} />
        <div className={`text-xs font-semibold ${muted ? "text-slate-500" : "text-slate-800"}`}>
          {title}
        </div>
      </div>
      <div className="text-[11px] text-slate-500 mb-2">{subtitle}</div>
      {items.length === 0 ? (
        <div className="text-[11px] text-slate-400 italic px-2"
             data-testid={`${testid}-empty`}>
          {emptyHint}
        </div>
      ) : (
        <ul className="flex flex-wrap gap-1.5"
            data-testid={`${testid}-list`}>
          {items.map((r) => (
            <li
              key={r.id}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border ${tone}`}
              data-testid={`${testid}-item-${r.id}`}
            >
              {r.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
